import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Head from 'next/head'
import { useMediaQuery } from 'react-responsive'

import { getFavouriteModel, updateModelFavourite } from 'store/actions/models.action'
import { notifyToast } from 'utils/responseUtils'

import ModelItem from './ModelItem'
import FilterTags from 'components/FilterTags'
import Dropdown from 'components/Dropdown'
import ModelsLoader from './ModelsLoader'

import { modelsTagsList } from './data'
import { Button } from '@components/ui/button'
import { ArrowLeftIcon } from 'lucide-react'
import { useRouter } from 'next/router'

const SORT_OPTIONS = [
  { label: 'Publish at', value: 'created_at' },
  { label: 'Rate', value: 'ratings' },
  { label: 'Popularity', value: 'run_count' },
  { label: 'Title', value: 'name' },
]

const ModelsComponent = ({
  filter,
  search,
  onFilterChange,
  isFavorite,
  filterClassNames = '',
  sortClassNames = '',
  explorePage,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobile = useMediaQuery({ maxWidth: 600 })
  const router = useRouter()
  const { favoriteLoading, favoriteModels, loading, data: models } = useSelector((state) => state.models)
  const user = useSelector((state) => state.auth.user)

  const [filterTag, setFilterTag] = useState(filter || 'Top')
  const [sortOrder, setSortOrder] = useState('asc')
  const [sortBy, setSortBy] = useState('created_at')

  const favoriteIds = useMemo(() => favoriteModels?.map((item) => item.id) || [], [favoriteModels])

  const rawData = useMemo(() => (isFavorite ? favoriteModels : models), [isFavorite, favoriteModels, models])

  const sortedData = useMemo(() => {
    if (!rawData) return []

    let filteredData = rawData

    // First apply search filter if search term exists
    if (search && search.trim()) {
      const searchTerm = search.toLowerCase().trim()
      filteredData = rawData.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.description?.toLowerCase().includes(searchTerm) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(searchTerm))
      )
    }

    // Then filter by tag (only if not searching or if search didn't return results)
    if (!search || search.trim() === '') {
      filteredData =
        filterTag === 'All' || !filterTag ? filteredData : filteredData.filter((item) => item.tags?.includes(filterTag))
    }

    // Finally sort the filtered data
    return [...filteredData].sort((a, b) => {
      if (sortBy === 'name' || sortBy === 'created_at') {
        return sortOrder === 'asc' ? b[sortBy]?.localeCompare(a[sortBy]) : a[sortBy]?.localeCompare(b[sortBy])
      } else {
        const numA = parseFloat(a[sortBy]) || 0
        const numB = parseFloat(b[sortBy]) || 0
        return sortOrder === 'asc' ? numA - numB : numB - numA
      }
    })
  }, [rawData, sortBy, sortOrder, filterTag, search])

  useEffect(() => {
    // Only fetch favorite models  data is not already present, and not currently loading
    if (!favoriteModels && !favoriteLoading) {
      dispatch(getFavouriteModel())
    }
  }, [dispatch, favoriteModels, favoriteLoading])

  useEffect(() => {
    if (filter && filter !== filterTag) {
      setFilterTag(filter)
    }
  }, [filter, filterTag])

  const handleToggleFavorite = useCallback(
    (id, favoriteStatus, name) => {
      if (!user) {
        return dispatch(notifyToast('success', `${t('model.favorite-need-login')}!`))
      }

      updateModelFavourite(id, favoriteStatus, () => {
        dispatch(notifyToast('success', `${t(`model.${favoriteStatus ? '' : 'un'}favorite-success`)} ${name} !`))
        dispatch(getFavouriteModel())
      })
    },
    [dispatch, t, user]
  )

  const handleSortChange = useCallback(
    (value) => {
      if (value === sortBy) {
        const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
        setSortOrder(nextOrder)
      } else {
        setSortBy(value)
        setSortOrder('asc')
      }
    },
    [sortBy, sortOrder]
  )

  const handleFilterChange = useCallback(
    (newFilter) => {
      setFilterTag(newFilter)
      if (onFilterChange) {
        onFilterChange(newFilter)
      }
    },
    [onFilterChange]
  )

  const schemaJSON = useMemo(() => {
    if (!explorePage || !sortedData) return null
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Explore 100+ AIs',
      description: 'Discover and explore over 100 AI models tailored for various applications.',
      url: 'https://styley.ai/explore-models',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: sortedData.map((model, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'CreativeWork',
            name: model.name,
            description: model.description,
            url: `https://styley.ai/model/${model.alias}`,
          },
        })),
      },
    })
  }, [explorePage, sortedData])

  return (
    <div id="ai-models-explore" className="models-component-container">
      {explorePage && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaJSON }} />
        </Head>
      )}

      <div className="flex w-full justify-between ">
        <div>
          <h1 className="text-2xl font-bold">
            {search && search.trim() ? `Search Results for "${search}"` : 'Explore 100+ AIs'}
          </h1>
          {search && search.trim() && (
            <p className="text-gray-600 mt-1">
              {sortedData?.length > 0 ? `${sortedData.length} models found` : 'No models found'}
            </p>
          )}
        </div>
        {sortedData?.length > 0 && (
          <div className={`hidden lg:flex ${sortClassNames}`}>
            <Dropdown
              options={SORT_OPTIONS}
              selectedOptions={sortBy}
              dropContainerStyle="min-w-min max-w-[150px] md:min-w-[120px]"
              buttonStyle={isMobile ? '!px-2 !py-1' : ''}
              downArrStyle="md:!w-[12px] md:!h-[12px]"
              dropdownMenuStyle={`${
                isMobile ? '!min-w-min !left-0 !right-2 !py-2 px-2 !bottom-8' : '!min-w-[140px] !py-3 px-2 !right-0'
              } w-full !left-0`}
              onSelect={handleSortChange}
              dropdownLabel={SORT_OPTIONS.find((item) => item.value === sortBy)?.label || ''}
              uniqueId="Sort:"
              sortDropdown
              sortOrder={sortOrder}
              multipleSelect={false}
              showSelection={false}
            />
          </div>
        )}
      </div>

      {/* Only show filter tags when not searching */}
      {(!search || search.trim() === '') && (
        <div className="w-full mt-5 justify-center item-center">
          <FilterTags
            tagsList={modelsTagsList}
            assetsFolder="explore-model"
            activeTab={filterTag}
            setActiveTab={handleFilterChange}
            carousel
            classNames={`w-full ${filterClassNames}`}
            fullWidth
          />
        </div>
      )}

      {loading ? (
        <ModelsLoader />
      ) : sortedData?.length > 0 ? (
        <div className="grid grid-cols-1 h-full mb-20 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-12">
          {sortedData?.map((model) => (
            <ModelItem
              key={model.id}
              model={model}
              isFavorite={favoriteIds.includes(model.id)}
              toggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {search && search.trim() ? 'No results found' : 'No models available'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md">
              {search && search.trim()
                ? `We couldn't find any models matching "${search}". Try adjusting your search terms or browse our featured models.`
                : 'No models are currently available. Please try again later.'}
            </p>
            {search && search.trim() && (
              <Button onClick={() => router.back()}>
                <ArrowLeftIcon className="w-4 h-4" /> Go Back
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ModelsComponent
